/**
 * Sentry browser SDK for SafeGeo Admin SPA (Vite + React Router).
 * Errors only by default - no Session Replay / high traces (matches Remix SafeGeo).
 * Set VITE_SENTRY_DSN at build time (CF Pages env).
 */
import * as Sentry from "@sentry/react";
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router-dom";
import { useEffect } from "react";
import { screenKeyForPath } from "./page-title";

const dsn = (import.meta.env.VITE_SENTRY_DSN || "").trim();
const environment =
  (import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE || "production").trim();

let booted = false;

export function initSentry(): boolean {
  if (booted || !dsn) return false;
  booted = true;

  Sentry.init({
    dsn,
    environment,
    sendDefaultPii: false,
    integrations: [
      Sentry.reactRouterV7BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
    ],
    // Low sample - Admin is low traffic; prefer errors over perf volume.
    tracesSampleRate: Number(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE || 0.1),
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    initialScope: {
      tags: { product: "safegeo-admin", surface: "shopify-admin-spa" },
    },
  });
  return true;
}

export function setSentryScreen(pathname: string): void {
  if (!booted && !dsn) return;
  const key = screenKeyForPath(pathname);
  Sentry.setTag("screen", key);
  Sentry.setContext("safegeo_screen", { path: pathname, screen: key });
}

export function setSentryShopContext(input: {
  shop?: string | null;
  plan?: string | null;
}): void {
  if (!booted && !dsn) return;
  if (input.shop) {
    Sentry.setUser({ id: input.shop, username: input.shop });
  }
  if (input.plan) Sentry.setTag("plan", input.plan);
}

export { Sentry };
